import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

export interface UserProject {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  url?: string;
  github_url?: string;
  technologies?: string[];
  created_at: string;
  updated_at: string;
}

export const useUserProjects = (userId?: string) => {
  const { user } = useUser();
  const [projects, setProjects] = useState<UserProject[]>([]);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (!targetUserId) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const fetchProjects = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_projects')
          .select('*')
          .eq('user_id', targetUserId as any)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching projects:', error);
          return;
        }

        setProjects((data as any) || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();

    // Listen for profile data updates
    const handleProfileUpdate = () => {
      fetchProjects();
    };

    window.addEventListener('profileDataUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileDataUpdated', handleProfileUpdate);
    };
  }, [targetUserId]);

  const addProject = async (project: Omit<UserProject, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_projects')
        .insert({ ...project, user_id: user.id } as any)
        .select()
        .single();

      if (error) throw error;

      setProjects(prev => [(data as any), ...prev]);
      return data;
    } catch (error) {
      console.error('Error adding project:', error);
      throw error;
    }
  };

  const updateProject = async (id: string, updates: Partial<UserProject>) => {
    try {
      const { data, error } = await supabase
        .from('user_projects')
        .update(updates as any)
        .eq('id', id as any)
        .select()
        .single();

      if (error) throw error;

      setProjects(prev => prev.map(p => p.id === id ? (data as any) : p));
      return data;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  const deleteProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_projects')
        .delete()
        .eq('id', id as any);

      if (error) throw error;

      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  };

  return {
    projects,
    loading,
    addProject,
    updateProject,
    deleteProject
  };
};